import {
    getResourceFlowMetric,
    getResourceFlowMetricSettings,
    withFlowUserType,
} from 'src/app/dashboards/mopa-resource-flow/resource-flow.models';
import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import {
    FlowDashboardData,
    getFlowAxis,
} from 'src/app/services/flow-dashboard/flow-dashboard.models';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardResourcesSqlMap } from '../../mopa-resources-detailed/sql-maps/biviz-dashboard-resources-datasource-sql-map';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

/**
 * Resource Flow by-date loader. Split, Group and filters use the same Resources SQL map/compiler as
 * the classic Resources dashboards. KPI tiles use their own global data loader, so chart data
 * remains scoped to the interactive Split and Group views.
 *
 * `average per dau` deliberately keeps parity with the classic `resources-by-date` loader: SUM /
 * daily active users. `average per deu` instead uses distinct players with a positive flow
 * on the corresponding side. Percentiles retain the classic row-level APPROX_QUANTILES definition.
 */

export interface ResourceFlowByDateDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

export class ResourceFlowByDateDataLoaderFactory extends Factory<
    DataLoader,
    ResourceFlowByDateDataLoaderFactoryOptions
> {
    override FACTORY_CLASS = 'ResourceFlowByDateDataLoaderFactory';

    getInstance(options: ResourceFlowByDateDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                if (getFlowAxis(queryContext) !== 'date') {
                    return;
                }

                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const resourcesSqlMap = BiVizDashboardResourcesSqlMap.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    resourcesSqlMap.columns
                );
                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const resourcesSQL =
                    BiVizDashboardResourcesSqlMap.sqlStatementsCompiler.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options
                    );

                const currency =
                    queryContext.settings[
                        QueryContextSettingID.RESOURCE_FLOW_CURRENCY
                    ] ?? 'gems';
                const { valueMode, population, percentiles } =
                    getResourceFlowMetricSettings(queryContext);
                const metric = getResourceFlowMetric(queryContext);

                if (valueMode === 'percentile' && !percentiles.length) {
                    throw new Error(
                        'Resource Flow: percentile mode needs at least one selected percentile'
                    );
                }

                const splitsByPercentile =
                    valueMode === 'percentile' && percentiles.length > 1;
                // Average and Percentile both offer the DAU population, and both then need the
                // users-activity table.
                const usesDauPopulation =
                    (valueMode === 'average' || valueMode === 'percentile') &&
                    population === 'dau';

                const usersdashActivity = BiVizDashboardUsersActivity;
                const usersdashActivityDsColumns = usesDauPopulation
                    ? DataLoader.separatePresentAndMissingDims(
                          queryContext.datasourceColumns,
                          usersdashActivity.sqlMap.columns
                      )
                    : undefined;
                const usersdashActivitySQL = usersdashActivityDsColumns
                    ? usersdashActivity.compileSQLStatements(
                          usersdashActivityDsColumns.present,
                          queryContext.settings,
                          options
                      )
                    : undefined;

                const resourcesTablePath = generateTableFullPath(
                    options.gcpProjectId,
                    resourcesSqlMap.dataset,
                    resourcesSqlMap.tableId
                );
                const usersdashActivityTablePath = generateTableFullPath(
                    options.gcpProjectId,
                    usersdashActivity.sqlMap.dataset,
                    usersdashActivity.sqlMap.tableId
                );

                // User Type filters flow and DAU by the same daily flag.
                const userType =
                    queryContext.settings[
                        QueryContextSettingID.FLOW_USER_TYPE
                    ] ?? 'all';
                const resourcesWhere = withFlowUserType(
                    resourcesSQL.where,
                    userType,
                    'resources'
                );
                const usersActivityWhere = usersdashActivitySQL
                    ? withFlowUserType(
                          usersdashActivitySQL.where,
                          userType,
                          'activity'
                      )
                    : '';
                const buildQuery = (): string => {
                    if (valueMode === 'total') {
                        return `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow - currency: ${currency}, mode: total

WITH base AS
(
  SELECT
    ${resourcesSQL.splitSelect}
    ds.date
    , ds.${currency}_inflow
    , ds.${currency}_outflow
    ${resourcesSQL.bucketSelect ? `, ${resourcesSQL.bucketSelect}` : ''}
  FROM \`${resourcesTablePath}\` ds
  ${resourcesSQL.join}
  ${resourcesWhere}
)

SELECT
    ${resourcesSQL.splitGroupBy}${
        resourcesSQL.bucketGroupBy
    }FORMAT_DATE('%Y-%m-%d', DATE(${resourcesSQL.dateTrunc()})) AS date
  , ROUND(SUM(${currency}_inflow),  2) AS inflow
  , ROUND(SUM(${currency}_outflow), 2) AS outflow
FROM base
GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}date
ORDER BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}date
`;
                    }

                    if (valueMode === 'flow records') {
                        return `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow - currency: ${currency}, mode: flow records

WITH base AS
(
  SELECT
    ${resourcesSQL.splitSelect}
    ds.date
    , ds.${currency}_inflow
    , ds.${currency}_outflow
    ${resourcesSQL.bucketSelect ? `, ${resourcesSQL.bucketSelect}` : ''}
  FROM \`${resourcesTablePath}\` ds
  ${resourcesSQL.join}
  ${resourcesWhere}
)

SELECT
    ${resourcesSQL.splitGroupBy}${
        resourcesSQL.bucketGroupBy
    }FORMAT_DATE('%Y-%m-%d', DATE(${resourcesSQL.dateTrunc()})) AS date
  , COUNTIF(${currency}_inflow > 0)  AS inflow
  , COUNTIF(${currency}_outflow > 0) AS outflow
FROM base
GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}date
ORDER BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}date
`;
                    }

                    if (valueMode === 'players') {
                        return `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow - currency: ${currency}, mode: players

WITH base AS
(
  SELECT
    ${resourcesSQL.splitSelect}
    ds.date
    , ds.player_id
    , ds.${currency}_inflow
    , ds.${currency}_outflow
    ${resourcesSQL.bucketSelect ? `, ${resourcesSQL.bucketSelect}` : ''}
  FROM \`${resourcesTablePath}\` ds
  ${resourcesSQL.join}
  ${resourcesWhere}
)

SELECT
    ${resourcesSQL.splitGroupBy}${
        resourcesSQL.bucketGroupBy
    }FORMAT_DATE('%Y-%m-%d', DATE(${resourcesSQL.dateTrunc()})) AS date
  , COUNT(DISTINCT IF(${currency}_inflow > 0, player_id, NULL))  AS inflow
  , COUNT(DISTINCT IF(${currency}_outflow > 0, player_id, NULL)) AS outflow
FROM base
GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}date
ORDER BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}date
`;
                    }

                    if (valueMode === 'average' && population === 'deu') {
                        // `deu` divides by the players with a positive flow on that side, so each side
                        // gets its own denominator.
                        return `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow - currency: ${currency}, mode: average per deu

WITH base AS
(
  SELECT
    ${resourcesSQL.splitSelect}
    ds.date
    , ds.player_id
    , ds.${currency}_inflow
    , ds.${currency}_outflow
    ${resourcesSQL.bucketSelect ? `, ${resourcesSQL.bucketSelect}` : ''}
  FROM \`${resourcesTablePath}\` ds
  ${resourcesSQL.join}
  ${resourcesWhere}
)

SELECT
    ${resourcesSQL.splitGroupBy}${
        resourcesSQL.bucketGroupBy
    }FORMAT_DATE('%Y-%m-%d', DATE(${resourcesSQL.dateTrunc()})) AS date
  , ROUND(SAFE_DIVIDE(
        SUM(${currency}_inflow),
        COUNT(DISTINCT IF(${currency}_inflow > 0, player_id, NULL))
    ), 2) AS inflow
  , ROUND(SAFE_DIVIDE(
        SUM(${currency}_outflow),
        COUNT(DISTINCT IF(${currency}_outflow > 0, player_id, NULL))
    ), 2) AS outflow
FROM base
GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}date
ORDER BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}date
`;
                    }

                    if (valueMode === 'average' && population === 'dau') {
                        // The Resources denominator follows cohort splits while datasource-specific
                        // splits reuse the same cohort DAU for every resource group. Group/Bucket
                        // remains a presentation layer and therefore does not alter the active-user
                        // population.
                        return `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow - currency: ${currency}, mode: average per dau

WITH base AS
(
  SELECT
    ${resourcesSQL.splitSelect}
    ${resourcesSQL.datasourceSpecificSplitSelect}
    ${resourcesSQL.cohortSplitSelect}
    ds.date
    , ds.${currency}_inflow
    , ds.${currency}_outflow
    ${resourcesSQL.bucketSelect ? `, ${resourcesSQL.bucketSelect}` : ''}
  FROM \`${resourcesTablePath}\` ds
  ${resourcesSQL.join}
  ${resourcesWhere}
)
, resourcesAgg AS
(
  SELECT ${resourcesSQL.splitGroupBy}${
      resourcesSQL.bucketGroupBy
  }${resourcesSQL.dateTrunc()} AS date
    , SUM(${currency}_inflow)  AS sum_inflow
    , SUM(${currency}_outflow) AS sum_outflow
  FROM base
  GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}date
)
, active_users AS
(
  SELECT
    ${resourcesSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    date
    , sum_daily_active_users
  FROM
  (
    SELECT
      ${resourcesSQL.cohortSplitGroupBy}${resourcesSQL.dateTrunc()} AS date
      , SUM(daily_active_users) AS sum_daily_active_users
    FROM
    (
      SELECT
        ${
            usersdashActivitySQL?.splitGroupBy
                ? usersdashActivitySQL.splitSelect.replace(
                      'AS split_group',
                      'AS cohort_split_group'
                  )
                : ''
        }date
        , COUNT(DISTINCT player_id) AS daily_active_users
      FROM \`${usersdashActivityTablePath}\` ds
      ${usersdashActivitySQL?.join ?? ''}
      ${usersActivityWhere}
      GROUP BY ${resourcesSQL.cohortSplitGroupBy}date
    )
    GROUP BY ${resourcesSQL.cohortSplitGroupBy}date
  )
  ${
      resourcesSQL.is_in_context_split_datasource_specific_columns
          ? `CROSS JOIN (SELECT DISTINCT ${resourcesSQL.datasourceSpecificSplitGroupAlias} FROM base)`
          : ''
  }
)

SELECT
    ${
        resourcesSQL.splitGroup
            ? 'resourcesAgg.split_group AS split_group, '
            : ''
    }${
        resourcesSQL.bucketGroup
            ? 'resourcesAgg.bucket_group AS bucket_group, '
            : ''
    }FORMAT_DATE('%Y-%m-%d', DATE(resourcesAgg.date)) AS date
  , ROUND(SAFE_DIVIDE(resourcesAgg.sum_inflow, active_users.sum_daily_active_users), 2)  AS inflow
  , ROUND(SAFE_DIVIDE(resourcesAgg.sum_outflow, active_users.sum_daily_active_users), 2) AS outflow
FROM resourcesAgg
LEFT JOIN active_users
  ON resourcesAgg.date = active_users.date
  ${
      resourcesSQL.splitGroup
          ? 'AND resourcesAgg.split_group = active_users.split_group'
          : ''
  }
ORDER BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}date
`;
                    }

                    if (valueMode === 'percentile' && population === 'deu') {
                        // A percentile describes one player, so rows are first summed per player and
                        // date bucket. `deu` keeps only the players with a positive flow on that side
                        // (APPROX_QUANTILES skips NULL), so the opposite side's players cannot drag the
                        // percentile to zero.
                        return `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow - currency: ${currency}, mode: P${percentiles.join(
                            ' + P'
                        )} per deu

WITH base AS
(
  SELECT
    ${resourcesSQL.splitSelect}
    ds.date
    , ds.player_id
    , ds.${currency}_inflow
    , ds.${currency}_outflow
    ${resourcesSQL.bucketSelect ? `, ${resourcesSQL.bucketSelect}` : ''}
  FROM \`${resourcesTablePath}\` ds
  ${resourcesSQL.join}
  ${resourcesWhere}
)
, perPlayer AS
(
  SELECT ${resourcesSQL.splitGroupBy}${
      resourcesSQL.bucketGroupBy
  }${resourcesSQL.dateTrunc()} AS date
    , player_id
    , SUM(${currency}_inflow)  AS inflow
    , SUM(${currency}_outflow) AS outflow
  FROM base
  GROUP BY ${resourcesSQL.splitGroupBy}${
      resourcesSQL.bucketGroupBy
  }date, player_id
)
, resourcesAgg AS
(
  SELECT ${resourcesSQL.splitGroupBy}${
      resourcesSQL.bucketGroupBy
  }date${percentiles
      .map(
          (percentile) => `
    , APPROX_QUANTILES(IF(inflow > 0, inflow, NULL), 100)[OFFSET(${percentile})]   AS quantile_inflow_${percentile}
    , APPROX_QUANTILES(IF(outflow > 0, outflow, NULL), 100)[OFFSET(${percentile})] AS quantile_outflow_${percentile}`
      )
      .join('')}
  FROM perPlayer
  GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}date
)

SELECT
    ${resourcesSQL.splitGroupBy}${
        resourcesSQL.bucketGroupBy
    }FORMAT_DATE('%Y-%m-%d', DATE(date)) AS date
  , quantile_inflow_${percentiles[0]}  AS inflow
  , quantile_outflow_${percentiles[0]} AS outflow${
      splitsByPercentile
          ? percentiles
                .map(
                    (percentile) => `
  , quantile_inflow_${percentile}
  , quantile_outflow_${percentile}`
                )
                .join('')
          : ''
  }
FROM resourcesAgg
ORDER BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}date
`;
                    }

                    if (valueMode === 'percentile' && population === 'dau') {
                        // `dau` percentiles every active player, reading a player without flow as zero,
                        // so the per-player values are first joined onto the whole active population.
                        return `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow - currency: ${currency}, mode: P${percentiles.join(
                            ' + P'
                        )} per dau

WITH base AS
(
  SELECT
    ${resourcesSQL.splitSelect}
    ${resourcesSQL.datasourceSpecificSplitSelect}
    ${resourcesSQL.cohortSplitSelect}
    ds.date
    , ds.player_id
    , ds.${currency}_inflow
    , ds.${currency}_outflow
    ${resourcesSQL.bucketSelect ? `, ${resourcesSQL.bucketSelect}` : ''}
  FROM \`${resourcesTablePath}\` ds
  ${resourcesSQL.join}
  ${resourcesWhere}
)
, perPlayer AS
(
  SELECT ${resourcesSQL.splitGroupBy}${
      resourcesSQL.bucketGroupBy
  }${resourcesSQL.dateTrunc()} AS date
    , player_id
    , SUM(${currency}_inflow)  AS inflow
    , SUM(${currency}_outflow) AS outflow
  FROM base
  GROUP BY ${resourcesSQL.splitGroupBy}${
      resourcesSQL.bucketGroupBy
  }date, player_id
)
, dauPlayers AS
(
  SELECT
    ${resourcesSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    date
    , player_id
  FROM
  (
    SELECT ${resourcesSQL.cohortSplitGroupBy}${resourcesSQL.dateTrunc()} AS date
      , player_id
    FROM
    (
      SELECT
        ${
            usersdashActivitySQL?.splitGroupBy
                ? usersdashActivitySQL.splitSelect.replace(
                      'AS split_group',
                      'AS cohort_split_group'
                  )
                : ''
        }date
        , player_id
      FROM \`${usersdashActivityTablePath}\` ds
      ${usersdashActivitySQL?.join ?? ''}
      ${usersActivityWhere}
      GROUP BY ${resourcesSQL.cohortSplitGroupBy}date, player_id
    )
    GROUP BY ${resourcesSQL.cohortSplitGroupBy}date, player_id
  )
  ${
      resourcesSQL.is_in_context_split_datasource_specific_columns
          ? `CROSS JOIN (SELECT DISTINCT ${resourcesSQL.datasourceSpecificSplitGroupAlias} FROM base)`
          : ''
  }
  ${
      resourcesSQL.bucketGroup
          ? 'CROSS JOIN (SELECT DISTINCT bucket_group FROM base)'
          : ''
  }
)
, population AS
(
  SELECT ${
      resourcesSQL.splitGroup ? 'dauPlayers.split_group AS split_group, ' : ''
  }${
      resourcesSQL.bucketGroup
          ? 'dauPlayers.bucket_group AS bucket_group, '
          : ''
  }dauPlayers.date AS date
    , COALESCE(perPlayer.inflow, 0)  AS inflow
    , COALESCE(perPlayer.outflow, 0) AS outflow
  FROM dauPlayers
  LEFT JOIN perPlayer
    ON perPlayer.date = dauPlayers.date
   AND perPlayer.player_id = dauPlayers.player_id
   ${
       /* A Split or Group expression over a nullable column yields a NULL group, which plain
          equality would never match: the whole group would read as zero flow. */
       resourcesSQL.splitGroup
           ? `AND IFNULL(perPlayer.split_group, '_NULL_') = IFNULL(dauPlayers.split_group, '_NULL_')`
           : ''
   }
   ${
       resourcesSQL.bucketGroup
           ? `AND IFNULL(perPlayer.bucket_group, '_NULL_') = IFNULL(dauPlayers.bucket_group, '_NULL_')`
           : ''
   }
)
, resourcesAgg AS
(
  SELECT ${resourcesSQL.splitGroupBy}${
      resourcesSQL.bucketGroupBy
  }date${percentiles
      .map(
          (percentile) => `
    , APPROX_QUANTILES(inflow, 100)[OFFSET(${percentile})]  AS quantile_inflow_${percentile}
    , APPROX_QUANTILES(outflow, 100)[OFFSET(${percentile})] AS quantile_outflow_${percentile}`
      )
      .join('')}
  FROM population
  GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}date
)

SELECT
    ${resourcesSQL.splitGroupBy}${
        resourcesSQL.bucketGroupBy
    }FORMAT_DATE('%Y-%m-%d', DATE(date)) AS date
  , quantile_inflow_${percentiles[0]}  AS inflow
  , quantile_outflow_${percentiles[0]} AS outflow${
      splitsByPercentile
          ? percentiles
                .map(
                    (percentile) => `
  , quantile_inflow_${percentile}
  , quantile_outflow_${percentile}`
                )
                .join('')
          : ''
  }
FROM resourcesAgg
ORDER BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}date
`;
                    }

                    // A new value mode or population must state its own query instead of silently
                    // inheriting another one.
                    throw new Error(
                        `Resource Flow: no query defined for value mode '${valueMode}' and population '${population}'`
                    );
                };

                const query = buildQuery();
                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                const resultRows = [...queryResponse.result];
                const rows = splitsByPercentile
                    ? resultRows.flatMap((row) =>
                          percentiles.map((percentile) => {
                              const label = `P${percentile}`;
                              return {
                                  ...row,
                                  inflow: row[`quantile_inflow_${percentile}`],
                                  outflow:
                                      row[`quantile_outflow_${percentile}`],
                                  split_group: resourcesSQL.splitGroup
                                      ? `${row.split_group ?? 'Total'} · ${label}`
                                      : label,
                                  ...(resourcesSQL.bucketGroup
                                      ? {
                                            bucket_group: `${
                                                row.bucket_group ?? 'Unknown'
                                            } · ${label}`,
                                        }
                                      : {}),
                              };
                          })
                      )
                    : resultRows;
                const dateAggregation =
                    queryContext.settings[
                        QueryContextSettingID.DATE_AGGREGATION
                    ] ?? 'day';
                XYChartAxis.fillMissingTimeGapRows(
                    rows,
                    queryContext,
                    'date',
                    dateAggregation,
                    {
                        dimensionKeys: [
                            ...(resourcesSQL.splitGroup || splitsByPercentile
                                ? ['split_group']
                                : []),
                            ...(resourcesSQL.bucketGroup
                                ? ['bucket_group']
                                : []),
                        ],
                    }
                );

                const data: FlowDashboardData = {
                    rows,
                    rangeAggregation: metric.rangeAggregation,
                };
                this.latestData = data;
                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
